import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Bar, BarVisual, Dot, Label, Wrapper } from './Styled'

type SliderState = 'init' | 'dragging'

interface LabeledSliderProps {
  min?: number
  max?: number
  value?: number
  showLabel?: boolean
  onChange?: (value: number) => void
  labelFormat?: (value: number, min: number, max: number) => string
  [key: string]: any
}

type TranslateStyle = CSSProperties & {
  '--translate'?: string
}

const LabeledSlider = ({
  min = 1,
  max = 2,
  value = 0,
  showLabel = true,
  onChange = (value: number) => {},
  labelFormat = (value: number, min: number, max: number) => `${value} / ${max - min + 1}`,
  ...props
}: LabeledSliderProps) => {
  const totalStep = max - min
  const [state, setState] = useState<SliderState>('init')
  const [pointer, setPointer] = useState<number | null>(null)
  const [maxTranslate, setMaxTranslate] = useState(0)
  const [translateBaseX, setTranslateBaseX] = useState(0)
  const [initialX, setInitialX] = useState(0)
  const [currentX, setCurrentX] = useState(0)

  const wrapperEl = useRef<HTMLDivElement>(null)
  const barEl = useRef<HTMLDivElement>(null)
  const dotEl = useRef<HTMLDivElement>(null)

  const onClickOutside = useCallback(() => {
    setState('init')
  }, [])

  useEffect(() => {
    window.addEventListener('pointerdown', onClickOutside)
    return () => {
      window.removeEventListener('pointerdown', onClickOutside)
    }
  })

  useLayoutEffect(() => {
    if (barEl.current == null) {
      return
    }

    const cb = () => {
      const wrapperRect = wrapperEl.current!.getBoundingClientRect()
      const barRect = barEl.current!.getBoundingClientRect()
      setMaxTranslate(barRect.width)
      setTranslateBaseX(barRect.x - wrapperRect.x)
    }
    const observer = new ResizeObserver(cb)
    observer.observe(barEl.current)
    return () => {
      observer.disconnect()
    }
  }, [])

  const prevEmittedValue = useRef<number | null>(null)
  const emitIfNot = (value: number) => {
    console.log(value)
    if (value !== prevEmittedValue.current) {
      onChange(value)
      prevEmittedValue.current = value
    }
  }

  const onPointerDown = (ev: React.PointerEvent<HTMLDivElement>) => {
    ev.stopPropagation()
    if (state === 'init') {
      // console.log(ev.currentTarget)
      barEl.current!.setPointerCapture(ev.pointerId)
      setPointer(ev.pointerId)
      setState('dragging')
      const barRect = barEl.current!.getBoundingClientRect()
      setInitialX(barRect.x)
      setCurrentX(ev.clientX)

      const previewValue = getDiff(ev.clientX - barRect.x) + min
      emitIfNot(previewValue)
    }
  }

  const onPointerMove = (ev: React.PointerEvent<HTMLDivElement>) => {
    if (state === 'dragging' && ev.pointerId === pointer) {
      // console.log(ev)
      setState('dragging')
      setCurrentX(ev.clientX)

      const previewValue = getDiff(ev.clientX - initialX) + min
      emitIfNot(previewValue)
    }
  }

  const onPointerUp = (ev: React.PointerEvent<HTMLDivElement>) => {
    if (state === 'dragging' && ev.pointerId === pointer) {
      // console.log(ev)
      setState('init')
    }
  }

  const onPointerCancel = (ev: React.PointerEvent<HTMLDivElement>) => {
    if (state === 'dragging' && ev.pointerId === pointer) {
      // console.log(ev)
      setState('init')
    }
  }

  const getDiff = (offset: number) => {
    const wrappedTranslate = Math.max(Math.min(offset, maxTranslate), 0)
    const diff = Math.round((wrappedTranslate / maxTranslate) * totalStep)
    return diff
  }

  const previewDiff = getDiff(currentX - initialX)
  const snappedTranslate = (maxTranslate / totalStep) * previewDiff
  const previewValue = previewDiff + min

  const actualTranslate = ((value - min) / totalStep) * maxTranslate

  return (
    <Wrapper {...props} ref={wrapperEl}>
      <Bar
        ref={barEl}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <BarVisual></BarVisual>
        <Dot
          ref={dotEl}
          style={
            state === 'dragging'
              ? ({ '--translate': `${snappedTranslate}px` } as TranslateStyle)
              : ({ '--translate': `${actualTranslate}px` } as TranslateStyle)
          }
        />
      </Bar>
      {showLabel && (
        <Label
          style={
            {
              '--translate': `${translateBaseX + (state === 'dragging' ? snappedTranslate : actualTranslate)}px`,
            } as TranslateStyle
          }
        >
          {labelFormat(state === 'dragging' ? previewValue : value, min, max)}
        </Label>
      )}
    </Wrapper>
  )
}

export default LabeledSlider
